import * as React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import classnames from 'classnames';
import LayoutHeader from '../header/LayoutHeader';
import styles from './LayoutPage.module.scss';
import { LayoutFooter } from 'src/modules/layout';
import { useLocale, OutputChannels } from 'src/modules/locale';
import {
  Breadcrumbs,
  BreadcrumbsProps,
} from 'src/modules/professionals/downloads/products/components/breadcrumbs/Breadcrumbs';
import { connect } from 'react-redux';
import { BaseStore } from 'src/store';
import getOneTrustScript from 'src/modules/cookies/getOneTrustScripts';
import useRedrawOnMountKey from 'src/utils/useRedrawOnMountKey';

interface LayoutPageProps {
  title?: string;
  description?: string;
  canonical?: string;
  topElement?: React.ReactNode;
  breadcrumbs?: BreadcrumbsProps['links'];
  className?: string;
  topPadding?: boolean;
  children: any;
  hasHeroBox: boolean;
  noIndex?: boolean;
  metaImage?: string;
}

const LayoutPage: React.FunctionComponent<LayoutPageProps> = ({
  children,
  title = 'Walter Knoll',
  description,
  topElement,
  breadcrumbs,
  canonical,
  className,
  topPadding = true,
  hasHeroBox,
  noIndex = false,
  metaImage,
}) => {
  const { asPath, pathname, locale } = useRouter();
  const { setLocale, origin, outputChannel } = useLocale();

  if (typeof window === 'undefined' && locale) {
    setLocale(locale);
  }

  const originUrl = locale === 'en' ? origin.concat('/en') : origin;

  // using a redrawKey here to ensure "Walter K." updates from "Walter Knowll"
  // after SSG pages are loaded
  const redrawKeyFooter = useRedrawOnMountKey();

  //let canonicalUrl = canonical ? originUrl + '/' + canonical : originUrl + asPath?.split('?')[0];
  let canonicalUrl = canonical
    ? originUrl + '/' + canonical
    : typeof window !== 'undefined'
    ? window.location.href
    : null;
  originUrl + asPath?.split('?')[0];

  //Remove last slash
  if (canonicalUrl?.slice(-1) === '/') {
    canonicalUrl = canonicalUrl.slice(0, -1);
  }

  const pageHasHero =
    hasHeroBox ||
    (!!pathname &&
      (['/', '/about/vision', '/about/quality', '/about/history'].includes(pathname) ||
        pathname.startsWith('/lp/')));

  //Remove HTML tags from the description
  const cleanDescription = description?.replace(/(<([^>]+)>)/gi, '');

  return (
    <div className={classnames(styles.container, className)}>
      <Head>
        <title>
          {title ? title : outputChannel === OutputChannels.US ? 'Walter K.' : 'Walter Knoll'}
        </title>
        {cleanDescription && <meta name="description" content={cleanDescription} />}
        <link rel="icon" href="/favicon.ico" />
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        {!!noIndex && <meta name="robots" content="noindex" />}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {[
          ['x-default', 'walterknoll.de/en'],
          ['de', 'walterknoll.de'],
          ['en', 'walterknoll.de/en'],
          ['en-au', 'walterknoll.com.au/en'],
          ['en-us', 'walter-k.com/en'],
        ].map(([hrefLang, domain]) => (
          <link
            key={hrefLang}
            rel="alternate"
            hrefLang={hrefLang}
            href={`https://www.${domain}${asPath}`}
          />
        ))}
        <meta property="og:title" content={title} />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta
          property="og:image"
          content={metaImage || origin + '/images/walter_knoll_opengraph_image.jpg'}
        />
        <meta property="og:type" content="website" />
        {cleanDescription && <meta property="og:description" content={cleanDescription} />}
        <meta property="og:locale" content={locale} />
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:title" content={title} />
        <meta name="p:domain_verify" content="c36753ec887edc7913a9d305356a3cf4" />
        {cleanDescription && <meta property="twitter:description" content={cleanDescription} />}
        {canonicalUrl && <meta property="twitter:url" content={canonicalUrl} />}

        {getOneTrustScript(outputChannel)}

        {outputChannel === OutputChannels.US ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'http://schema.org',
                '@type': 'Organization',
                name: 'Walter K',
                url: 'https://www.walter-k.com',
                sameAs: [
                  'https://www.facebook.com/WalterKinc',
                  'https://www.instagram.com/walterk_official/',
                  'https://www.youtube.com/channel/UCwy9XJ4kl0ObCplhT_2aeNQ',
                ],
                contactPoint: [
                  {
                    '@type': 'ContactPoint',
                    email: 'info@walter-k.com',
                    telephone: '+19179723470',
                    contactType: 'customer service',
                  },
                ],
                logo: 'https://www.walter-k.com/img/walter-knoll-logo-5c5d0415.svg',
              }),
            }}
          />
        ) : (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'http://schema.org',
                '@type': 'Organization',
                name: 'Walter Knoll',
                url: 'https://www.walterknoll.de',
                sameAs: [
                  'https://www.facebook.com/walterknoll.official',
                  'https://www.instagram.com/walterknoll_official/',
                  'https://www.youtube.com/user/walterknollonline',
                  'https://www.linkedin.com/company/walter-knoll',
                ],
                contactPoint: [
                  {
                    '@type': 'ContactPoint',
                    email: 'info@walterknoll.de',
                    telephone: '+497032208-0',
                    contactType: 'customer service',
                  },
                ],
                logo: 'https://www.walterknoll.de/images/logo.svg',
              }),
            }}
          />
        )}
      </Head>

      <LayoutHeader />
      {!pageHasHero ? <div className={styles.contentsPusher} /> : null}
      {!!topElement && topElement}
      {!!breadcrumbs && <Breadcrumbs links={breadcrumbs} />}
      <div
        className={classnames([
          styles.content,
          pageHasHero && styles.withHero,
          topPadding === false && styles.noTopPadding,
        ])}>
        {children}
      </div>
      <LayoutFooter key={redrawKeyFooter} />
    </div>
  );
};

const mapStateToProps = (state: BaseStore) => {
  return { hasHeroBox: !!state?.header?.heroBox };
};

export default connect(mapStateToProps)(LayoutPage);
